import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import {
  CAlert,
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCardTitle,
  CCol,
  CContainer,
  CImage,
  CRow,
  CSpinner,
  CBadge,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilChevronBottom,
  cilChevronTop,
  cilPhone,
  cilEnvelopeClosed,
  cilLightbulb,
  cilBraille,
} from '@coreui/icons'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { SplashScreen } from '@capacitor/splash-screen'
import PartnerInfoRow from '../../components/PartnerInfoRow'
import { buildApiUrl } from '../../utils/getConfiguration'
import InternalAppHeader from './components/InternalAppHeader'
import {
  sanitizeValue,
  handlePhoneClick,
  handleMailClick,
  readSupplierAssignment,
  filterPreferredSuppliers,
} from '../../utils/supplierHelpers'
import { useTranslation } from 'react-i18next'
import { PREMIUM_MEMBERSHIP_TYPES } from '../../utils/getConfiguration'

const DetailSection = ({
  title,
  content = '',
  isLocked = false,
  isExpanded = false,
  onToggle = () => {},
  previewLength = 180,
  lockedMessage,
  toggleLabel,
}) => {
  if (!content) {
    return null
  }

  if (isLocked) {
    return (
      <div className="detail-section mt-4">
        <CCardTitle className="detail-section__title">{title}</CCardTitle>
        <CAlert color="danger" className="mb-0">
          {lockedMessage}
        </CAlert>
      </div>
    )
  }

  const preview = String(content).trim()
  const displayText = isExpanded
    ? preview
    : `${preview.slice(0, previewLength)}${preview.length > previewLength ? '…' : ''}`

  return (
    <div className="detail-section mt-4">
      <div className="detail-section__header">
        <CCardTitle className="detail-section__title mb-0">{title}</CCardTitle>
        {preview.length > previewLength && (
          <CButton color="primary" size="sm" variant="outline" onClick={onToggle}>
            <CIcon icon={isExpanded ? cilChevronTop : cilChevronBottom} aria-hidden="true" />
            <span className="ms-1">{toggleLabel}</span>
          </CButton>
        )}
      </div>
      <div className="detail-section__text">
        <pre className="detail-section__pre">{displayText}</pre>
      </div>
    </div>
  )
}

DetailSection.propTypes = {
  title: PropTypes.string.isRequired,
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLocked: PropTypes.bool,
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
  previewLength: PropTypes.number,
  lockedMessage: PropTypes.string.isRequired,
  toggleLabel: PropTypes.string.isRequired,
}

const Detail = () => {
  const navigate = useNavigate()
  const [accountId] = useState(() => localStorage.getItem('account_ID') || '')
  const [taskId] = useState(() => localStorage.getItem('taskid') || '')
  const [membership] = useState(() => {
    if (typeof window === 'undefined') return 'FREE'
    return (
      window.localStorage.getItem('membershipType') ||
      window.localStorage.getItem('membership') ||
      'FREE'
    )
  })
  const [groupId] = useState(() => localStorage.getItem('grp_ID') || '')

  const [recommendation, setRecommendation] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [expandedSections, setExpandedSections] = useState({
    activities: false,
    requisites: false,
    risks: false,
  })
  const [preferredPartner, setPreferredPartner] = useState(null)
  const [preferredPartnerLoading, setPreferredPartnerLoading] = useState(false)
  const { t } = useTranslation()

  const normalizedMembership = useMemo(() => {
    const value = (membership || '').toString().trim().toUpperCase()
    if (value === 'ELITE') return 'ELITE'
    if (value === 'PRO') return 'PRO'
    return 'FREE'
  }, [membership])
  const isLocked = useMemo(() => {
    return !PREMIUM_MEMBERSHIP_TYPES.has(normalizedMembership)
  }, [normalizedMembership])

  const getPartnerTypeLabel = useCallback(
    (type) => {
      switch (type) {
        case 'F':
          return t('detail.partner.typeFavourite')
        case 'C':
          return t('detail.partner.typeCopy')
        case 'J':
          return t('detail.partner.typeJoint')
        default:
          return null
      }
    },
    [t],
  )

  const toggleSection = useCallback((section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }, [])

  const fetchRecommendation = useCallback(
    async ({ signal } = {}) => {
      if (!accountId || !taskId) {
        setRecommendation(null)
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)
      try {
        const response = await apiClient.get(buildApiUrl('reminders', { account_ID: accountId }), {
          signal,
        })
        const data = Array.isArray(response.data) ? response.data : []
        const match = data.find((item) => String(item?.reminder_ID) === String(taskId)) || null
        setRecommendation(match)
        if (!match) {
          setError('Unable to find requested reminder.')
        }
      } catch (requestError) {
        if (apiClient.isCancel(requestError)) return
        console.error('Error fetching recommendation detail', requestError)
        setError('Unable to load reminder detail. Please try again later.')
        setRecommendation(null)
      } finally {
        setLoading(false)
      }
    },
    [accountId, taskId],
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchRecommendation({ signal: controller.signal })

    if (window?.SplashScreen?.hide) {
      SplashScreen.hide({ fadeOutDuration: 200, autoHide: true }).catch((splashError) => {
        console.warn('Unable to hide splash screen on Detail load', splashError)
      })
    }

    return () => controller.abort()
  }, [fetchRecommendation])

  useEffect(() => {
    const assignment = readSupplierAssignment()
    const assignedId = sanitizeValue(assignment?.supplierId)

    if (!accountId || !groupId || !assignedId) {
      setPreferredPartner(null)
      return
    }

    const controller = new AbortController()

    const fetchPreferredPartner = async () => {
      try {
        setPreferredPartnerLoading(true)
        const params = { account_ID: accountId, reminder_ID: taskId, grp_ID: groupId }
        const response = await apiClient.get(buildApiUrl('supplierPref', params), {
          signal: controller.signal,
        })
        const preferredList = filterPreferredSuppliers(
          Array.isArray(response.data) ? response.data : [],
        )
        const match =
          preferredList.find((partner) => {
            const candidate = sanitizeValue(
              partner.supplier_id ?? partner.startup_id ?? partner.supplierId,
            )
            return candidate && candidate === assignedId
          }) || null

        if (match) {
          setPreferredPartner(match)
        } else {
          const fallbackName = sanitizeValue(assignment?.supplierName)
          const fallbackPhone = sanitizeValue(assignment?.supplierPhone)
          if (fallbackName || fallbackPhone) {
            setPreferredPartner({
              supplierName: fallbackName,
              supplierPhone: fallbackPhone,
              supplierMail: '',
              supplierPic: '',
              type: null,
            })
          } else {
            setPreferredPartner(null)
          }
        }
      } catch (prefError) {
        if (!apiClient.isCancel?.(prefError) && prefError?.name !== 'CanceledError') {
          console.error('Error fetching preferred partner detail', prefError)
        }
        setPreferredPartner(null)
      } finally {
        setPreferredPartnerLoading(false)
      }
    }

    fetchPreferredPartner()

    return () => controller.abort()
  }, [accountId, taskId, groupId])

  const detailCards = useMemo(() => {
    if (!recommendation) return []
    return [recommendation]
  }, [recommendation])

  if (loading) {
    return (
      <div className="detail-spinner">
        <CSpinner color="primary" variant="grow" style={{ width: '3rem', height: '3rem' }} />
      </div>
    )
  }

  if (error) {
    return (
      <div className="detail-wrapper min-vh-100 d-flex flex-column">
        <CContainer className="py-4 flex-grow-1">
          <InternalAppHeader title="Description" onBack={() => navigate(-1)} />
          <CRow className="justify-content-center g-4 mt-2">
            <CCol lg={10}>
              <CAlert color="danger" className="mb-0">
                {error}
              </CAlert>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    )
  }

  if (!recommendation) {
    return (
      <div className="detail-wrapper min-vh-100 d-flex flex-column">
        <CContainer className="py-4 flex-grow-1">
          <InternalAppHeader title="Description" onBack={() => navigate(-1)} />
          <CRow className="justify-content-center g-4 mt-2">
            <CCol lg={10}>
              <CAlert color="info" className="mb-0">
                {t('detail.empty.noReminder')}
              </CAlert>
            </CCol>
          </CRow>
        </CContainer>
      </div>
    )
  }

  return (
    <div className="detail-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader title={t('detail.header.title')} onBack={() => navigate(-1)} />

        <CRow className="justify-content-center g-4 mt-2">
          <CCol lg={10}>
            {detailCards.map((item) => {
              const damageValue = Number(item?.Damage) || 0
              const stepsRaw = item.steps ?? ''
              const stepsText =
                typeof stepsRaw === 'string' ? stepsRaw : String(stepsRaw ?? '').trim()
              const containsCallSupport = stepsText
                .toLowerCase()
                .includes('call professional support')
              const shouldShowPreferredWidget = preferredPartner && containsCallSupport
              const shouldShowFallbackWidget = !preferredPartner && containsCallSupport

              const partnerName =
                sanitizeValue(preferredPartner?.supplierName) || t('detail.partner.fallbackName')
              const partnerPhone = sanitizeValue(preferredPartner?.supplierPhone)
              const partnerMail = sanitizeValue(preferredPartner?.supplierMail)
              const rawPicture = sanitizeValue(preferredPartner?.supplierPic)
              const partnerPicture = rawPicture
                ? /^https?:\/\//i.test(rawPicture)
                  ? rawPicture
                  : `https://displatform.com/assets/suppliers/${rawPicture}`
                : ''
              const partnerTypeLabel = getPartnerTypeLabel(preferredPartner?.type)
              const partnerInfoLines = [
                partnerPhone ? t('suppliers.labels.phone', { value: partnerPhone }) : null,
                partnerMail ? t('suppliers.labels.email', { value: partnerMail }) : null,
              ].filter(Boolean)

              return (
                <CCard
                  key={item.reminder_ID}
                  className="detail-card__section border-0 shadow-sm mb-4"
                >
                  <CCardBody className="p-4 p-lg-5">
                    <CRow className="gy-4 align-items-start">
                      <CCol
                        md={4}
                        lg={3}
                        className="detail-card__image-col text-center text-md-start"
                      >
                        {item.pic_duplicate ? (
                          <CImage
                            rounded
                            loading="lazy"
                            src={item.pic_duplicate}
                            alt={item.Title}
                            className="detail-card__image"
                          />
                        ) : null}
                      </CCol>
                      <CCol md={8} lg={9}>
                        <h6 className="info-navbar__brand-title fw-semibold mb-3">{item.Title}</h6>
                        <CCardText className="mb-3">
                          <strong>{t('detail.sections.toBeDone')}</strong>
                          <br />
                          {item.Description}
                        </CCardText>
                        <div className="detail-section mt-4">
                          <CCardTitle className="detail-section__title">
                            {t('detail.sections.contact')}
                          </CCardTitle>
                          {preferredPartnerLoading ? (
                            <div className="detail-preferred-loading">
                              <CSpinner color="primary" size="sm" />
                              <span>{t('detail.partner.loading')}</span>
                            </div>
                          ) : shouldShowPreferredWidget ? (
                            <div className="detail-preferred-partner-card innovation-partner-card p-3">
                              <PartnerInfoRow
                                icon={
                                  <CIcon
                                    icon={cilLightbulb}
                                    size="lg"
                                    className="text-warning flex-shrink-0"
                                    aria-hidden="true"
                                  />
                                }
                                picture={partnerPicture}
                                title={partnerName}
                                badge={
                                  partnerTypeLabel ? (
                                    <CBadge color="info">{partnerTypeLabel}</CBadge>
                                  ) : null
                                }
                                lines={partnerInfoLines}
                                imageWidth={96}
                              />
                              <div className="d-flex gap-2 flex-wrap mt-3 detail-preferred-partner-actions">
                                {partnerPhone && (
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    onClick={() => handlePhoneClick(partnerPhone)}
                                    aria-label={t('suppliers.actions.call', { name: partnerName })}
                                  >
                                    <CIcon icon={cilPhone} />
                                  </CButton>
                                )}
                                {partnerMail && (
                                  <CButton
                                    color="primary"
                                    variant="outline"
                                    onClick={() => handleMailClick(partnerMail, partnerName)}
                                    aria-label={t('suppliers.actions.email', { name: partnerName })}
                                  >
                                    <CIcon icon={cilEnvelopeClosed} />
                                  </CButton>
                                )}
                              </div>
                            </div>
                          ) : shouldShowFallbackWidget ? (
                            <div className="detail-preferred-partner-card innovation-partner-card p-3 detail-preferred-partner-card--empty">
                              <div className="detail-preferred-partner-card__empty">
                                <CIcon icon={cilLightbulb} size="lg" className="text-warning" />
                                <div className="detail-preferred-partner-card__empty-text">
                                  <span className="detail-preferred-partner-card__empty-title">
                                    {t('detail.partner.emptyTitle')}
                                  </span>
                                  <span className="detail-preferred-partner-card__empty-caption">
                                    {t('detail.partner.emptyMessage')}
                                  </span>
                                </div>
                              </div>
                              <CButton
                                color="primary"
                                variant="outline"
                                size="sm"
                                onClick={() => navigate('/suppliersselection')}
                                aria-label={t('detail.partner.browseAria')}
                                className="detail-preferred-partner-card__empty-button"
                              >
                                <CIcon icon={cilBraille} size="xl" />
                              </CButton>
                            </div>
                          ) : (
                            <div className="detail-section__text">
                              <pre className="detail-section__pre">{stepsText}</pre>
                            </div>
                          )}
                        </div>
                        <CCardText className="mb-3">
                          <strong>{t('detail.damageRisk.label')}</strong>
                          <br />
                          {t('detail.damageRisk.value', {
                            value: damageValue.toLocaleString(),
                          })}
                        </CCardText>

                        <DetailSection
                          title={t('detail.sections.activities')}
                          content={item.Activities}
                          isLocked={isLocked}
                          isExpanded={expandedSections.activities}
                          onToggle={() => toggleSection('activities')}
                          lockedMessage={t('detail.sections.lockedMessage')}
                          toggleLabel={t('detail.sections.toggleLabel')}
                        />
                        <DetailSection
                          title={t('detail.sections.requisites')}
                          content={item.Requisities}
                          isLocked={isLocked}
                          isExpanded={expandedSections.requisites}
                          onToggle={() => toggleSection('requisites')}
                          lockedMessage={t('detail.sections.lockedMessage')}
                          toggleLabel={t('detail.sections.toggleLabel')}
                        />
                        <DetailSection
                          title={t('detail.sections.risks')}
                          content={item.Risks}
                          isLocked={isLocked}
                          isExpanded={expandedSections.risks}
                          onToggle={() => toggleSection('risks')}
                          lockedMessage={t('detail.sections.lockedMessage')}
                          toggleLabel={t('detail.sections.toggleLabel')}
                        />
                      </CCol>
                    </CRow>
                  </CCardBody>
                </CCard>
              )
            })}
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default Detail
